import React, {
  createContext,
  useReducer,
  useEffect,
  Reducer,
  FC,
} from 'react';
import decode from 'jwt-decode';
import { reducerWithLogger } from './util';
import accounts from './accounts';
import {
  setAccessToken,
  unsetAccessToken,
  setRefreshMethod,
} from './authFetch';

enum Action {
  RefreshRequest = 'ACCOUNT:REFRESH_REQUEST',
  RefreshSuccess = 'ACCOUNT:REFRESH_SUCCESS',
  RefreshFailure = 'ACCOUNT:REFRESH_FAILURE',
  RefreshUnauthorized = 'ACCOUNT:REFRESH_UNAUTHORIZED',
  LogoutRequest = 'ACCOUNT:LOGOUT_REQUEST',
  LogoutSuccess = 'ACCOUNT:LOGOUT_SUCCESS',
  LogoutFailure = 'ACCOUNT:LOGOUT_FAILURE',
  InitLocal = 'ACCOUNT:INIT_LOCAL',
  InitSession = 'ACCOUNT:INIT_SESSION',
  SetAge = 'ACCOUNT:SET_AGE',
}

interface RefreshRequestAction {
  type: Action.RefreshRequest;
}

interface RefreshSuccessAction {
  type: Action.RefreshSuccess;
  access_token: string;
  decoded: AuthToken;
}

interface RefreshFailureAction {
  type: Action.RefreshFailure;
  error: Error;
}

interface RefreshUnauthorizedAction {
  type: Action.RefreshUnauthorized;
}

interface LogoutRequestAction {
  type: Action.LogoutRequest;
}

interface LogoutSuccessAction {
  type: Action.LogoutSuccess;
}

interface LogoutFailureAction {
  type: Action.LogoutFailure;
  error: Error;
}

interface InitLocalAction {
  type: Action.InitLocal;
  accounts: LocalAccount[];
}

interface InitSessionAction {
  type: Action.InitSession;
  account?: SessionAccount;
  isUnder13?: boolean;
}

interface SetAgeAction {
  type: Action.SetAge;
  age: number;
}

type AccountAction =
  | RefreshRequestAction
  | RefreshSuccessAction
  | RefreshFailureAction
  | RefreshUnauthorizedAction
  | LogoutRequestAction
  | LogoutSuccessAction
  | LogoutFailureAction
  | InitLocalAction
  | InitSessionAction
  | SetAgeAction;

export enum MemberType {
  Teacher = 'teacher',
  Parent = 'parent',
  Homeschool = 'homeschool',
  Student = 'student',
}

export enum MemberRole {
  Admin = 'admin',
  Premium = 'premium',
  SchoolAdmin = 'school-admin',
}

export enum SocialProvider {
  Google = 'google',
  Facebook = 'facebook',
}

export interface AuthToken {
  dateLastLogin: string;
  sub: string;
  iat: number;
  email: string;
  groups: MemberType[];
  roles: MemberRole[];
  social: SocialProvider[];
}

interface AccountState {
  /** True if the context is currently awaiting a response to an operation. */
  loading: boolean;
  /** True if the initial refresh request has completed (or failed). */
  ready: boolean;
  error?: Error;
  token?: string;
  decoded?: AuthToken;
  /** A string representing the member's PRIMARY type, options are 'teacher', 'parent', 'homeschool'. */
  memberType: MemberType;
  /** True if the authenticated user is a site admin. */
  isAdmin?: boolean;
  /** True if the authenticated user is a premium member with an active subscription. */
  isPremium?: boolean;
  /** True if the authenticated user is an admin of a school account. */
  isSchoolAdmin?: boolean;
  isUnder13?: boolean;
  otherAccounts?: LocalAccount[];
}

type SessionAccount = Pick<
  AccountState,
  'decoded' | 'memberType' | 'isPremium' | 'isSchoolAdmin' | 'otherAccounts'
>;

interface LocalAccount {
  email?: string;
  membershipType: 'pro' | 'basic';
  userType: MemberType;
  socialSignupTypes: SocialProvider[];
  dateLastLogin?: number;
}

type AccountReducer = Reducer<AccountState, AccountAction>;

export type AccountContextProvider = Omit<AccountState, 'token' | 'decoded'> & {
  email?: string;
  memberId?: string;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  isType: (string) => boolean;
  setAge: (number) => void;
};

const SESSION_KEY = 'account';
const LOCAL_KEY = 'accountInfo';

const initialState: AccountState = {
  loading: false,
  ready: false,
  error: null,
  token: null,
  decoded: null,
  memberType: null,
  isAdmin: null,
  isPremium: null,
  isSchoolAdmin: null,
  isUnder13: null,
  otherAccounts: null,
};

const getPrimaryType = (groups: string[] = []): string | null => {
  if (groups.includes(MemberType.Homeschool)) {
    return MemberType.Homeschool;
  }
  if (groups.includes(MemberType.Teacher)) {
    return MemberType.Teacher;
  }
  if (groups.includes(MemberType.Parent)) {
    return MemberType.Parent;
  }
  if (groups.includes(MemberType.Student)) {
    return MemberType.Student;
  }

  return null;
};

const reducer = reducerWithLogger(
  (state: AccountState, action: AccountAction) => {
    switch (action.type) {
      case Action.RefreshRequest:
      case Action.LogoutRequest:
        return {
          ...state,
          loading: true,
        };

      case Action.RefreshSuccess:
        return {
          ...state,
          error: null,
          loading: false,
          ready: true,
          token: action.access_token,
          decoded: action.decoded,
          memberType: getPrimaryType(action.decoded?.groups),
          isAdmin: action.decoded?.roles.includes(MemberRole.Admin),
          isPremium: action.decoded?.roles.includes(MemberRole.Premium),
          isSchoolAdmin: action.decoded?.roles.includes(MemberRole.SchoolAdmin),
        };

      case Action.RefreshFailure:
        return {
          ...state,
          loading: false,
          ready: true,
          error: action.error,
        };

      case Action.RefreshUnauthorized:
      case Action.LogoutSuccess:
        return {
          ...initialState,
          loading: false,
          ready: true,
          otherAccounts: state.otherAccounts,
        };

      case Action.LogoutFailure:
        return {
          ...state,
          error: action.error,
          loading: false,
        };

      case Action.InitLocal:
        return {
          ...state,
          otherAccounts: action.accounts ?? [],
        };

      case Action.InitSession:
        return {
          ...state,
          ...action.account,
          isUnder13: action.isUnder13,
        };

      case Action.SetAge:
        return {
          ...state,
          isUnder13: action.age < 13,
        };

      default:
        return state;
    }
  }
);

const AccountContext = createContext<Partial<AccountContextProvider>>({});

const AccountProvider: FC = props => {
  const [state, dispatch] = useReducer<AccountReducer>(reducer, initialState);

  /**
   * Update the list of accounts used on this device in LocalStorage.
   */
  const updateLocalAccounts = (): void => {
    if (typeof window === 'undefined') return;

    let accounts;
    try {
      accounts = JSON.parse(window.localStorage.getItem(LOCAL_KEY)) ?? [];
    } catch {
      // Error accessing local storage
      accounts = [];
    }

    // If account info exists, update it
    if (state.decoded != null) {
      const info: LocalAccount = {
        email: state.decoded?.email,
        membershipType: state.isPremium ? 'pro' : 'basic',
        userType: state.memberType,
        socialSignupTypes: state.decoded?.social ?? [],
        dateLastLogin: new Date().getTime(),
      };

      accounts = accounts
        .filter(item => item.email !== info.email)
        .concat(info);

      // Sort accounts by last login date
      accounts.sort(function(a, b) {
        if (!Object.prototype.hasOwnProperty.call(a, 'dateLastLogin')) {
          a.dateLastLogin = 0;
        }
        if (!Object.prototype.hasOwnProperty.call(b, 'dateLastLogin')) {
          b.dateLastLogin = 0;
        }
        return b.dateLastLogin - a.dateLastLogin;
      });

      try {
        window.localStorage.setItem(LOCAL_KEY, JSON.stringify(accounts));
      } catch {
        // Ignore local storage errors, most likely out of space or security error
      }
    }

    dispatch({ type: Action.InitLocal, accounts });
  };

  /**
   * Initialize account data from SessionStorage before auth finishes.
   */
  const initSession = (): void => {
    if (typeof window === 'undefined') return;

    let account = null;
    try {
      account = JSON.parse(window.sessionStorage.getItem(SESSION_KEY));
    } catch {
      // Most likely a security error with accessing sessionStorage, ignore
    }

    let isUnder13 = null;
    if (/isUnder13\s*?=\s*?1/.test(document.cookie)) {
      isUnder13 = true;
    } else if (/is13OrOlder\s*?=\s*?1/.test(document.cookie)) {
      isUnder13 = false;
    }

    dispatch({ type: Action.InitSession, account, isUnder13 });
  };

  /**
   * Save account info state in session storage.
   */
  const updateSession = (): void => {
    if (typeof window === 'undefined') return;

    try {
      window.sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({
          decoded: state.decoded,
          memberType: state.memberType,
          isPremium: state.isPremium,
          isSchoolAdmin: state.isSchoolAdmin,
          otherAccounts: state.otherAccounts,
        })
      );
    } catch {
      // Most likely a security error with accessing sessionStorage, ignore
    }
  };

  /**
   * Refresh the current user's session.
   */
  const refresh: AccountContextProvider['refresh'] = async () => {
    dispatch({ type: Action.RefreshRequest });

    try {
      const { access_token } = await accounts.refresh();
      const decoded = decode<AuthToken>(access_token);

      setAccessToken(access_token);
      dispatch({
        type: Action.RefreshSuccess,
        access_token,
        decoded,
      });
    } catch (error) {
      if (error.status === 401) {
        unsetAccessToken();
        dispatch({ type: Action.RefreshUnauthorized });
      } else {
        console.error(error);
        dispatch({ type: Action.RefreshFailure, error });
      }
      throw error;
    }
  };

  /**
   * Log the current user out of the session.
   */
  const logout: AccountContextProvider['logout'] = async () => {
    dispatch({ type: Action.LogoutRequest });

    try {
      await accounts.logout();
      unsetAccessToken();
      dispatch({ type: Action.LogoutSuccess });
    } catch (error) {
      dispatch({ type: Action.LogoutFailure, error });
      throw error;
    }
  };

  /**
   * Check if the user is of the specified type.
   */
  const isType: AccountContextProvider['isType'] = memberType =>
    state?.decoded?.groups.includes(memberType) ?? false;

  /**
   * Sets the users age to inform COPPA compliance.
   */
  const setAge: AccountContextProvider['setAge'] = age => {
    dispatch({ type: Action.SetAge, age });
  };

  useEffect(() => {
    initSession();
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    refresh().catch(function() {});
    setRefreshMethod(refresh);
  }, []);

  useEffect(() => {
    updateSession();
    updateLocalAccounts();
  }, [state.decoded]);

  const value: AccountContextProvider = {
    error: state.error,
    loading: state.loading,
    ready: state.ready,
    memberType: state.memberType,
    isAdmin: state.isAdmin,
    isPremium: state.isPremium,
    isSchoolAdmin: state.isSchoolAdmin,
    isUnder13: state.isUnder13,
    otherAccounts: state.otherAccounts,
    memberId: state.decoded?.sub,
    email: state.decoded?.email,
    refresh,
    logout,
    isType,
    setAge,
  };

  return (
    <AccountContext.Provider value={value}>
      {props.children}
    </AccountContext.Provider>
  );
};
AccountProvider.displayName = 'AccountProvider';

export { AccountContext as default, AccountProvider };
